import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { scrollHorizontally } from '@lib/scroller';
import EventEmitter, { EVENT_TYPES } from '@lib/eventEmitter';
import classNames from 'classnames';

export default class HorizontalScroller extends Component {
  static displayName = 'HorizontalScroller';
  static propTypes = {
    children: PropTypes.node,
    id: PropTypes.string.isRequired,
  };

  state = {
    locked: false,
    showLeft: false,
    showRight: false,
  };

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown, true);

    EventEmitter.on(EVENT_TYPES.imgGalleryOpen, this.onImgGalleryOpen);
    EventEmitter.on(EVENT_TYPES.imgGalleryClosed, this.onImgGalleryClosed);

    this.updateButtons(0);

    this.wrapper.addEventListener('focus', this.onFocus);
    this.wrapper.addEventListener('blur', this.onBlur);
  }

  componentDidUpdate(prevProps) {
    const oldKeys = React.Children.toArray(prevProps.children)
      .map((el) => el?.key)
      .join('');
    const newKeys = React.Children.toArray(this.props.children)
      .map((el) => el?.key)
      .join('');
    if (oldKeys === newKeys) return;
    this.slider.scrollLeft = 0;
    this.updateButtons(0);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown, true);

    EventEmitter.off(EVENT_TYPES.imgGalleryOpen, this.onImgGalleryOpen);
    EventEmitter.off(EVENT_TYPES.imgGalleryClosed, this.onImgGalleryClosed);

    this.wrapper?.removeEventListener('focus', this.onFocus);
    this.wrapper?.removeEventListener('blur', this.onBlur);
  }

  onImgGalleryOpen = () => {
    this.setState({ locked: true });
  };

  onImgGalleryClosed = () => {
    this.setState({ locked: false });
  };

  onKeyDown = (event) => {
    if (HorizontalScroller.focusedScroller !== this.props.id) return;

    if (event.key === 'ArrowLeft') return this.scrollLeft();
    if (event.key === 'ArrowRight') return this.scrollRight();
  };

  getSnapPoints = () => {
    const children = Array.from(this.slider?.children || []);
    const maxScrollLeft = this.slider.scrollWidth - this.wrapper.offsetWidth;
    const firstOffset = children[0]?.offsetLeft || 0;
    const points = children
      .map((child) => child.offsetLeft - firstOffset)
      .filter((point) => {
        return point >= 0 && point <= maxScrollLeft;
      });

    points.push(maxScrollLeft);

    return [...new Set(points)].sort((a, b) => a - b);
  };

  getVisibleCardCount = () => {
    if (window.matchMedia('(min-width: 992px)').matches) return 3;
    if (window.matchMedia('(min-width: 768px)').matches) return 2;
    return 1;
  };

  getCurrentSnapIndex = (snapPoints, currentPosition) => {
    const threshold = 1;
    let currentIndex = 0;

    snapPoints.forEach((point, index) => {
      if (point <= currentPosition + threshold) {
        currentIndex = index;
      }
    });

    return currentIndex;
  };

  getSnapTarget = (direction) => {
    const snapPoints = this.getSnapPoints();
    if (snapPoints.length === 0) return 0;

    const currentPosition = this.slider.scrollLeft;
    const currentIndex = this.getCurrentSnapIndex(snapPoints, currentPosition);
    const visibleCardCount = this.getVisibleCardCount();

    if (direction === 'right') {
      const targetIndex = Math.min(
        currentIndex + visibleCardCount,
        snapPoints.length - 1,
      );
      return snapPoints[targetIndex];
    }

    const targetIndex = Math.max(currentIndex - visibleCardCount, 0);
    return snapPoints[targetIndex];
  };

  scrollLeft = () => {
    const newPosition = this.getSnapTarget('left');
    scrollHorizontally(this.slider, newPosition);
    this.updateButtons(newPosition);
  };

  scrollRight = () => {
    const newPosition = this.getSnapTarget('right');
    scrollHorizontally(this.slider, newPosition);
    this.updateButtons(newPosition);
  };

  updateButtons = (position) => {
    const showLeft = position > 0;
    const showRight =
      position + this.wrapper.offsetWidth < this.slider.scrollWidth;
    if (
      showLeft !== this.state.showLeft ||
      showRight !== this.state.showRight
    ) {
      this.setState({ showLeft, showRight });
    }
  };

  onFocus = () => {
    HorizontalScroller.focusedScroller = this.props.id;
  };

  onBlur = () => {
    HorizontalScroller.focusedScroller = null;
  };

  render() {
    return (
      <div
        className="ac-horiz-scroller ch-mh--n2"
        ref={(wrapper) => (this.wrapper = wrapper)}
        tabIndex={0} // Makes the scroller focusable
      >
        <div
          className={classNames('ac-horiz-scroller__slider', {
            'ac-horiz-scroller--locked': this.state.locked,
          })}
          ref={(slider) => (this.slider = slider)}
        >
          {this.props.children}
        </div>
        {this.state.showLeft && (
          <button
            className="js-off--hide ac-hidden--sl ac-horiz-scroller__prev"
            onClick={this.scrollLeft}
            type="button"
          >
            <span className="ch-reader">Previous</span>
          </button>
        )}
        {this.state.showRight && (
          <button
            className="js-off--hide ac-hidden--sl ac-horiz-scroller__next"
            onClick={this.scrollRight}
            type="button"
          >
            <span className="ch-reader">Next</span>
          </button>
        )}
      </div>
    );
  }
}
